import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notesAPI, formatDate, stripHtml, NOTE_TYPES } from '../utils/api';
import './Dashboard.css';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid | list

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { archived: showArchived };
      if (search) params.search = search;
      if (filterType) params.type = filterType;
      if (filterTag) params.tag = filterTag;
      const res = await notesAPI.getAll(params);
      setNotes(res.data.notes);
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
  }, [search, filterType, filterTag, showArchived]);

  useEffect(() => {
    const t = setTimeout(fetchNotes, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchNotes, search]);

  useEffect(() => {
    notesAPI.getStats().then(r => setStats(r.data.stats)).catch(() => {});
  }, [notes.length]);

  const handleDelete = async (id) => {
    try {
      await notesAPI.delete(id);
      setNotes(prev => prev.filter(n => n._id !== id));
      setDeleteId(null);
    } catch (err) {
      alert('Failed to delete note');
    }
  };

  const handlePin = async (note) => {
    try {
      await notesAPI.update(note._id, { isPinned: !note.isPinned });
      setNotes(prev => prev.map(n => n._id === note._id ? { ...n, isPinned: !n.isPinned } : n));
    } catch {}
  };

  const handleArchive = async (note) => {
    try {
      await notesAPI.update(note._id, { isArchived: !note.isArchived });
      setNotes(prev => prev.filter(n => n._id !== note._id));
    } catch {}
  };

  const allTags = [...new Set(notes.flatMap(n => n.tags))];
  const pinnedNotes = notes.filter(n => n.isPinned);
  const regularNotes = notes.filter(n => !n.isPinned);

  return (
    <div className="dashboard" data-sidebar={sidebarOpen}>
      {/* ── Sidebar ── */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-logo">
          <span className="logo-icon">◈</span>
          {sidebarOpen && <span className="logo-text">NOTE <span>APEX</span></span>}
        </div>

        <button className="btn btn-primary new-note-btn" onClick={() => navigate('/note/new')}>
          {sidebarOpen ? '+ New Note' : '+'}
        </button>

        <nav className="sidebar-nav">
          <div className="nav-section-label">{sidebarOpen && 'WORKSPACE'}</div>
          <button className={`nav-item ${!filterType && !filterTag && !showArchived ? 'active' : ''}`}
            onClick={() => { setFilterType(''); setFilterTag(''); setShowArchived(false); }}>
            <span>🏠</span>{sidebarOpen && <span>All Notes</span>}
            {sidebarOpen && stats && <span className="nav-count">{stats.total}</span>}
          </button>
          {NOTE_TYPES.map(t => (
            <button key={t.value} className={`nav-item ${filterType === t.value ? 'active' : ''}`}
              onClick={() => { setFilterType(t.value); setShowArchived(false); }}>
              <span>{t.icon}</span>{sidebarOpen && <span>{t.label}</span>}
              {sidebarOpen && stats?.byType?.[t.value] && <span className="nav-count">{stats.byType[t.value]}</span>}
            </button>
          ))}
          <button className={`nav-item ${showArchived ? 'active' : ''}`}
            onClick={() => { setShowArchived(true); setFilterType(''); setFilterTag(''); }}>
            <span>🗄️</span>{sidebarOpen && <span>Archived</span>}
            {sidebarOpen && stats?.archived > 0 && <span className="nav-count">{stats.archived}</span>}
          </button>
        </nav>

        {sidebarOpen && allTags.length > 0 && (
          <div className="sidebar-tags">
            <div className="nav-section-label">TAGS</div>
            <div className="tags-list">
              {allTags.slice(0, 12).map(tag => (
                <button key={tag} className={`tag-btn ${filterTag === tag ? 'active' : ''}`}
                  onClick={() => setFilterTag(filterTag === tag ? '' : tag)}>
                  # {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="sidebar-user">
          {sidebarOpen && (
            <div className="user-info">
              <div className="user-avatar">{user?.name?.[0]?.toUpperCase()}</div>
              <div>
                <div className="user-name">{user?.name}</div>
                <div className="user-email">{user?.email}</div>
              </div>
            </div>
          )}
          <button className="btn btn-ghost btn-icon logout-btn" onClick={logout} title="Logout">⏻</button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="main-content">
        {/* Header */}
        <header className="dash-header">
          <div className="header-left">
            <button className="btn btn-ghost btn-icon" onClick={() => setSidebarOpen(p => !p)} title="Toggle sidebar">
              ☰
            </button>
            <div className="search-wrapper">
              <span className="search-icon">🔍</span>
              <input
                className="search-input"
                placeholder="Search notes..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button className="search-clear" onClick={() => setSearch('')}>✕</button>}
            </div>
          </div>
          <div className="header-right">
            <div className="view-toggle">
              <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}>⊞</button>
              <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>☰</button>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/note/new')}>+ New</button>
          </div>
        </header>

        {/* Content */}
        <div className="notes-area">
          {/* Page Title */}
          <div className="page-title">
            <h2>
              {showArchived ? '🗄️ Archive' :
               filterType ? NOTE_TYPES.find(t => t.value === filterType)?.icon + ' ' + NOTE_TYPES.find(t => t.value === filterType)?.label :
               filterTag ? `# ${filterTag}` : '🏠 All Notes'}
            </h2>
            <span className="note-count">{notes.length} note{notes.length !== 1 ? 's' : ''}</span>
          </div>

          {loading ? (
            <div className="loading-state"><div className="spinner" style={{ width: 36, height: 36 }} /></div>
          ) : notes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">{search ? '🔍' : '📝'}</div>
              <h3>{search ? 'No notes found' : 'No notes yet'}</h3>
              <p>{search ? `No results for "${search}"` : 'Create your first note to get started!'}</p>
              {!search && <button className="btn btn-primary" onClick={() => navigate('/note/new')}>+ Create Note</button>}
            </div>
          ) : (
            <>
              {pinnedNotes.length > 0 && (
                <div className="notes-group">
                  <div className="group-label">📌 Pinned</div>
                  <div className={`notes-${viewMode}`}>
                    {pinnedNotes.map(note => <NoteCard key={note._id} note={note} viewMode={viewMode} onPin={handlePin} onArchive={handleArchive} onDelete={setDeleteId} onClick={() => navigate(`/note/${note._id}`)} />)}
                  </div>
                </div>
              )}
              {regularNotes.length > 0 && (
                <div className="notes-group">
                  {pinnedNotes.length > 0 && <div className="group-label">📋 Notes</div>}
                  <div className={`notes-${viewMode}`}>
                    {regularNotes.map(note => <NoteCard key={note._id} note={note} viewMode={viewMode} onPin={handlePin} onArchive={handleArchive} onDelete={setDeleteId} onClick={() => navigate(`/note/${note._id}`)} />)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Delete Modal */}
      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-icon">🗑️</div>
            <h3>Delete Note?</h3>
            <p>This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Note Card Component ──────────────────────────────────────
function NoteCard({ note, viewMode, onPin, onArchive, onDelete, onClick }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const preview = stripHtml(note.content).slice(0, 120);

  const typeColors = { note: 'badge-note', journal: 'badge-journal', code: 'badge-code', todo: 'badge-todo' };
  const typeIcons = { note: '📝', journal: '📔', code: '💻', todo: '✅' };

  return (
    <div className={`note-card ${viewMode === 'list' ? 'note-card-list' : ''}`}
      style={{ borderTop: `2px solid ${note.isPinned ? 'var(--accent)' : 'transparent'}` }}
      onClick={onClick}>
      <div className="card-header">
        <span className={`badge ${typeColors[note.type] || 'badge-note'}`}>{typeIcons[note.type]} {note.type}</span>
        <div className="card-actions" onClick={e => e.stopPropagation()}>
          {note.isPinned && <span className="pin-indicator">📌</span>}
          <button className="card-menu-btn" onClick={e => { e.stopPropagation(); setMenuOpen(p => !p); }}>⋮</button>
          {menuOpen && (
            <div className="card-menu">
              <button onClick={() => { onPin(note); setMenuOpen(false); }}>{note.isPinned ? '📌 Unpin' : '📌 Pin'}</button>
              <button onClick={() => { onArchive(note); setMenuOpen(false); }}>{note.isArchived ? '📤 Unarchive' : '🗄️ Archive'}</button>
              <button className="danger" onClick={() => { onDelete(note._id); setMenuOpen(false); }}>🗑️ Delete</button>
            </div>
          )}
        </div>
      </div>
      <h3 className="card-title">{note.title || 'Untitled'}</h3>
      {viewMode === 'grid' && preview && <p className="card-preview">{preview}</p>}
      {note.tags?.length > 0 && (
        <div className="card-tags">
          {note.tags.slice(0, 3).map(t => <span key={t} className="tag"># {t}</span>)}
        </div>
      )}
      <div className="card-footer">
        <span className="card-date">{formatDate(note.updatedAt)}</span>
        {note.wordCount > 0 && <span className="card-words">{note.wordCount} words</span>}
      </div>
    </div>
  );
}
